/**
 * Place hierarchy used to browse projects by location (see Place / Project Place /
 * Unlocated in apiv3/docs/domain/projects/CONTEXT.md). Pure: no Vue, store or router.
 */

export interface PlaceRef {
  slug: string
  name: string
  level: string
}

export interface PlaceableProject {
  project_id: number
  project_name: string
  place_path?: PlaceRef[] | null
}

export interface PlaceNode<P extends PlaceableProject> {
  slug: string
  name: string
  level: string
  slugs: string[]
  children: Map<string, PlaceNode<P>>
  projects: P[]
  total: number
}

export interface PlaceTile {
  name: string
  level: string
  count: number
  /** The Place the tile shows. */
  slugs: string[]
  /** Where opening it lands: past any skipped single-child Places. */
  target: string[]
}

export interface PlaceCrumb {
  name: string
  slugs: string[] | null
}

export type PlaceLevel<P extends PlaceableProject> =
  | { kind: 'level'; crumbs: PlaceCrumb[]; tiles: PlaceTile[]; projects: P[] }
  | { kind: 'redirect'; slugs: string[] }

export const UNLOCATED: PlaceRef = { slug: 'unlocated', name: 'Unlocated', level: 'unlocated' }

/** Level of a Custom Place: a Project's own drawn or uploaded location, named after it. */
export const CUSTOM_LEVEL = 'area'

const newNode = <P extends PlaceableProject>(ref: PlaceRef, slugs: string[]): PlaceNode<P> => ({
  ...ref,
  slugs,
  children: new Map(),
  projects: [],
  total: 0,
})

const placePath = <P extends PlaceableProject>(project: P): PlaceRef[] =>
  project.place_path?.length ? project.place_path : [UNLOCATED]

export const buildPlaceTree = <P extends PlaceableProject>(projects: P[]): PlaceNode<P> => {
  const root = newNode<P>({ slug: '', name: 'All projects', level: 'root' }, [])
  for (const project of projects) {
    const path = placePath(project)
    let node = root
    root.total++
    for (const ref of path) {
      let child = node.children.get(ref.slug)
      if (!child) {
        child = newNode<P>(ref, [...node.slugs, ref.slug])
        node.children.set(ref.slug, child)
      }
      child.total++
      node = child
    }
    node.projects.push(project)
  }
  foldOwnPlaces(root)
  return root
}

/**
 * A Custom Place holding nothing but its own Project is not a level to browse into: the Project
 * shows at the Place above instead. Bottom-up, so a Place whose sub-places all fold keeps them as projects.
 */
const foldOwnPlaces = <P extends PlaceableProject>(node: PlaceNode<P>) => {
  for (const [slug, child] of node.children) {
    foldOwnPlaces(child)
    if (child.level === CUSTOM_LEVEL && child.children.size === 0 && child.projects.length === 1) {
      node.projects.push(child.projects[0])
      node.children.delete(slug)
    }
  }
}

/** A Place with no projects of its own and a single child Place is skipped while browsing. */
const isPassthrough = <P extends PlaceableProject>(node: PlaceNode<P>) =>
  node.slugs.length > 0 && node.projects.length === 0 && node.children.size === 1

const collapse = <P extends PlaceableProject>(node: PlaceNode<P>): PlaceNode<P> => {
  let current = node
  while (isPassthrough(current)) current = current.children.values().next().value!
  return current
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)

const orderedChildren = <P extends PlaceableProject>(node: PlaceNode<P>): PlaceNode<P>[] => {
  const children = [...node.children.values()]
  const located = children.filter(c => c.slug !== UNLOCATED.slug).sort(byName)
  const unlocated = children.filter(c => c.slug === UNLOCATED.slug)
  return [...located, ...unlocated]
}

const tileOf = <P extends PlaceableProject>(node: PlaceNode<P>): PlaceTile => ({
  name: node.name,
  level: node.level,
  count: node.total,
  slugs: node.slugs,
  target: collapse(node).slugs,
})

const tilesOf = <P extends PlaceableProject>(node: PlaceNode<P>): PlaceTile[] => orderedChildren(node).map(tileOf)

/** Every Place below `root`, depth-first in browsing order, with its depth for indenting. */
export const flattenPlaces = <P extends PlaceableProject>(root: PlaceNode<P>): (PlaceTile & { depth: number })[] => {
  const out: (PlaceTile & { depth: number })[] = []
  const walk = (node: PlaceNode<P>, depth: number) => {
    for (const child of orderedChildren(node)) {
      out.push({ ...tileOf(child), depth })
      walk(child, depth + 1)
    }
  }
  walk(root, 0)
  return out
}

/**
 * Resolves a URL place path to what the level shows, or to the nearest browsable
 * ancestor when the path is unknown, empty, or a skipped (passthrough) Place.
 */
export const resolvePlaceLevel = <P extends PlaceableProject>(root: PlaceNode<P>, slugs: string[]): PlaceLevel<P> => {
  const trail: PlaceNode<P>[] = [root]
  for (const slug of slugs) {
    const next = trail[trail.length - 1].children.get(slug)
    if (!next) break
    trail.push(next)
  }

  const node = trail[trail.length - 1]
  if (trail.length !== slugs.length + 1 || isPassthrough(node)) {
    const ancestor = [...trail].reverse().find(n => !isPassthrough(n)) ?? root
    return { kind: 'redirect', slugs: ancestor.slugs }
  }

  return {
    kind: 'level',
    crumbs: trail.map((n, i) => ({
      name: n.name,
      slugs: i === trail.length - 1 || isPassthrough(n) ? null : n.slugs,
    })),
    tiles: tilesOf(node),
    projects: [...node.projects].sort((a, b) => a.project_name.localeCompare(b.project_name)),
  }
}

/** Where a Project is, without its own Custom Place (which only repeats the Project's name). */
export const placeLabel = <P extends PlaceableProject>(project: P): string => {
  const path = placePath(project)
  const own = path[path.length - 1]?.level === CUSTOM_LEVEL ? path.slice(0, -1) : path
  return own.map(ref => ref.name).join(' › ')
}

export interface PlaceBranch<P extends PlaceableProject> {
  key: string
  /** Skipped single-child Places are merged into one row: "United States › California". */
  name: string
  count: number
  unlocated: boolean
  children: PlaceBranch<P>[]
  projects: P[]
}

const branchOf = <P extends PlaceableProject>(node: PlaceNode<P>, keep: (project: P) => boolean): PlaceBranch<P> => {
  const names = [node.name]
  let current = node
  while (isPassthrough(current)) {
    current = current.children.values().next().value!
    names.push(current.name)
  }
  const children = placeBranches(current, keep)
  const projects = current.projects.filter(keep).sort((a, b) => a.project_name.localeCompare(b.project_name))
  return {
    key: current.slugs.join('/'),
    name: names.join(' › '),
    count: projects.length + children.reduce((sum, child) => sum + child.count, 0),
    unlocated: node.slug === UNLOCATED.slug,
    children,
    projects,
  }
}

/** The Place hierarchy as an expandable tree; Places with no kept projects are dropped. */
export const placeBranches = <P extends PlaceableProject>(
  node: PlaceNode<P>,
  keep: (project: P) => boolean = () => true,
): PlaceBranch<P>[] => orderedChildren(node).map(child => branchOf(child, keep)).filter(branch => branch.count > 0)
